import React, { useMemo, useEffect, useState, useRef } from "react";
import { Button, Form, Tabs, Tab, Row, Col } from "react-bootstrap";
import SitesMap from "./SitesMap";
import SiteCard from "./SiteCard";
import { useDispatch, useSelector } from "react-redux";
import EmptySites from "./EmptySites";
import { setCropOptions, setSites } from "reduxStore/slices/siteSlice";
import {
  setBusinesses,
  setFarmsBusinesses,
} from "reduxStore/slices/globalSlice";
import {
  setSearch,
  setDebouncedSearch,
  setShowMap,
} from "reduxStore/slices/siteFilterSlice";
import {
  NO_SHARED_SITES,
  SITE_FETCH_ERROR,
  BUSINESS_AND_FARMS_FETCH_ERROR,
  PLANTED_ACRES_OPTIONS_FETCH_ERROR,
  LABEL_SEARCH_SITES,
} from "configurations/MessageConstants";
import SiteService from "services/SiteService";
import CardSkeleton from "components/common/CardSkeleton";
import SiteFilter from "./SiteFilter";
import ShareSites from "./ShareSites";
import { setSelectedSite } from "reduxStore/slices/siteSlice";
import { setSelectedPlantedAcre } from "reduxStore/slices/plantedAcresSlice";
import { setPlantedAcres } from "reduxStore/slices/plantedAcresSlice";
import "styles/MySites.css";
import { useLocation, useNavigate } from "react-router-dom";
import CustomPagination from "components/common/CustomPagination";
import UserService from "services/UserService";
import debounce from "debounce";
import { SHARE_TYPES, TAB_KEYS } from "configurations/Constants";
import PlantedAcresService from "services/PlantedAcresService";
import { resetPlantedAcresFilters } from "reduxStore/slices/plantedAcresFilterSlice";
import EmptyShare from "components/common/EmptyShare";

const MySites = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const abortControllerRef = useRef(null);
  const {
    showMap,
    selectedBusiness,
    selectedFarms,
    search,
    debouncedSearch,
    isFilterApplied,
  } = useSelector((state) => state.siteFilter);
  const location = useLocation();
  const [showSiteFilter, setShowSiteFilter] = useState(false);
  const [activeTab, setActiveTab] = useState(TAB_KEYS.REGISTERED_SITES);
  const [showShareSite, setShowShareSite] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [registeredCurrentPage, setRegisteredCurrentPage] = useState(1);
  const [sharedCurrentPage, setSharedCurrentPage] = useState(1);
  const [paginatedMySites, setPaginatedMySites] = useState([]);
  const [paginatedSharedSites, setPaginatedSharedSites] = useState([]);

  const debouncedSetSearch = useMemo(
    () =>
      debounce((searchValue) => {
        dispatch(setDebouncedSearch(searchValue));
      }, 400),
    [dispatch]
  );

  useEffect(() => {
    debouncedSetSearch(search);
  }, [search, debouncedSetSearch]);

  useEffect(() => {
    if (location.state?.fromBack) {
      navigate(location.pathname, { state: { fromBack: false } });
      return;
    }
    getSites();
    dispatch(setSelectedSite(null));
    dispatch(setSelectedPlantedAcre(null));
    dispatch(setPlantedAcres(null));
    setRegisteredCurrentPage(1);
    setSharedCurrentPage(1);
  }, [dispatch, navigate, debouncedSearch, selectedBusiness, selectedFarms]);

  useEffect(() => {
    if (location.state?.fromBack) {
      navigate(location.pathname, { state: { fromBack: false } });
      return;
    }
    getFarmsBusinesses();
    getCropOptions();
  }, [dispatch, navigate]);

  const getSites = async () => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    setIsLoading(true);
    let params = `search=${debouncedSearch}`;

    if (selectedFarms && selectedFarms.length > 0) {
      const farmNames = selectedFarms.map((farm) => farm.value).join(",");
      params += `&farmName=${farmNames}`;
    }

    if (selectedBusiness && selectedBusiness.length > 0) {
      const businessNames = selectedBusiness
        .map((business) => business.value)
        .join(",");
      params += `&businessName=${businessNames}`;
    }
    try {
      const response = await SiteService.getSites(
        params,
        abortController.signal
      );
      if (!abortController.signal.aborted) {
        dispatch(setSites(response));
      }
    } catch (error) {
      if (!abortController.signal.aborted) {
        console.error(SITE_FETCH_ERROR, error);
      }
    } finally {
      if (
        abortControllerRef.current === abortController &&
        !abortController.signal.aborted
      ) {
        setIsLoading(false);
      }
    }
  };

  const getFarmsBusinesses = async () => {
    try {
      const entities = await UserService.getFarmsBusinesses();
      dispatch(setBusinesses(entities.businesses));
      dispatch(setFarmsBusinesses(entities.farmsBusinesses));
    } catch (error) {
      console.error(BUSINESS_AND_FARMS_FETCH_ERROR, error);
    }
  };

  const getCropOptions = async () => {
    try {
      const response = await PlantedAcresService.getCropOptions();
      dispatch(setCropOptions(response));
    } catch (error) {
      console.error(PLANTED_ACRES_OPTIONS_FETCH_ERROR, error);
    }
  };

  const sites = useSelector((state) => state.site.sites);
  const selectedSite = useSelector((state) => state.site.selectedSite);

  useEffect(() => {
    if (selectedSite && selectedSite.id && sites.length > 0) {
      const updatedSite = sites.find((site) => site.id === selectedSite.id);
      if (updatedSite && updatedSite !== selectedSite) {
        dispatch(setSelectedSite(updatedSite));
      }
    }
  }, [sites, selectedSite, dispatch]);

  const mySites = useMemo(
    () => sites?.filter((site) => !site.isShared) || [],
    [sites]
  );
  const sharedSites = useMemo(
    () => sites?.filter((site) => site.isShared) || [],
    [sites]
  );

  const handleSiteClick = (site) => {
    dispatch(setSelectedSite(site));
    dispatch(resetPlantedAcresFilters());
    navigate(`/sites/${site.id}/planted-acres`);
  };

  const toggleMapView = () => {
    dispatch(setShowMap(!showMap));
    dispatch(setSelectedSite(null));
  };

  const handleFilterToggle = () => {
    setShowSiteFilter(!showSiteFilter);
  };

  const handleRegisteredPageChange = (page) => {
    setRegisteredCurrentPage(page);
  };

  const handleSharedPageChange = (page) => {
    setSharedCurrentPage(page);
  };

  const handleTabSelect = (tabKey) => {
    setActiveTab(tabKey);
    // Reset current page to 1 when switching tabs
    if (tabKey === TAB_KEYS.REGISTERED_SITES) {
      setRegisteredCurrentPage(1);
    } else if (tabKey === TAB_KEYS.SHARED_SITES) {
      setSharedCurrentPage(1);
    }
  };

  return (
    <div>
      <div className="d-flex align-items-center gap-2">
        <h5 className="m-0">My Sites</h5>
        <Button
          variant="outline-dark"
          size="sm"
          className={`${
            isFilterApplied ? "filter-icon-active" : "filter-icon"
          } pl-3 position-absolute end-0 rounded-0`}
          onClick={handleFilterToggle}
          aria-label="Sites Filter"
          title="Sites Filter"
        >
          <i className="fa fa-filter" aria-hidden="true"></i>
        </Button>
      </div>
      <div className="my-3">
        {/* Share/Register on left, Search + Map View on right for larger screens */}
        {/* Share/Register + Map View on first row, Search on second row for smaller screens */}
        <div className="d-flex flex-column flex-md-row justify-content-md-between gap-2">
          <div className="d-flex justify-content-between">
            <div className="d-flex gap-3">
              <Button
                variant="link"
                className="text-decoration-none p-0"
                onClick={() => {
                  setShowShareSite(true);
                }}
                disabled={mySites.length === 0}
              >
                <i className="fa fa-share-alt"></i> Share
              </Button>
              <Button
                variant="link"
                className="text-decoration-none p-0"
                onClick={() => navigate("/site-registration")}
              >
                <i className="fa fa-plus-circle"></i> Register
              </Button>
            </div>

            {/* Map View on same row at the end for smaller screens */}
            <div className="d-flex d-md-none align-items-center gap-2 flex-shrink-0">
              <span>Map View</span>
              <Form.Check
                type="switch"
                id="custom-switch"
                checked={showMap}
                onChange={toggleMapView}
                aria-label="Map View"
              />
            </div>
          </div>

          {/* Search + Map View together on right for larger screens */}
          <div className="d-none d-md-flex gap-3">
            <div
              className="position-relative flex-grow-1"
              style={{ minWidth: "300px" }}
            >
              <Form.Control
                type="text"
                placeholder={LABEL_SEARCH_SITES}
                className="input-underline pe-5"
                aria-label="Search"
                value={search}
                onChange={(e) => dispatch(setSearch(e.target.value))}
              />
              {search ? (
                <i
                  className="fa fa-times filter-clear-search"
                  onClick={() => dispatch(setSearch(""))}
                ></i>
              ) : (
                <i className="fa fa-search filter-search"></i>
              )}
            </div>
            <div className="d-flex align-items-center gap-2 flex-shrink-0">
              <span>Map View</span>
              <Form.Check
                type="switch"
                id="custom-switch-desktop"
                checked={showMap}
                onChange={toggleMapView}
                aria-label="Map View"
              />
            </div>
          </div>
        </div>

        {/*Search on second row taking full width for smaller screens */}
        <div className="d-block d-md-none mt-2">
          <div className="position-relative">
            <Form.Control
              type="text"
              placeholder={LABEL_SEARCH_SITES}
              className="input-underline pe-5"
              aria-label="Search"
              value={search}
              onChange={(e) => dispatch(setSearch(e.target.value))}
            />
            {search ? (
              <i
                className="fa fa-times filter-clear-search"
                onClick={() => dispatch(setSearch(""))}
              ></i>
            ) : (
              <i className="fa fa-search filter-search"></i>
            )}
          </div>
        </div>
      </div>
      {showMap ? (
        <div className="sites-map-container mt-3">
          <SitesMap />
        </div>
      ) : (
        <div className="d-flex flex-column">
          <Tabs
            id="controlled-tabs"
            activeKey={activeTab}
            onSelect={handleTabSelect}
            className="mb-3 border-bottom"
          >
            <Tab
              eventKey={TAB_KEYS.REGISTERED_SITES}
              title={
                <span>
                  Registered Sites <span className="registered-site-dot" />
                </span>
              }
            >
              {isLoading || !sites ? (
                <CardSkeleton numberOfCards={3} />
              ) : mySites.length === 0 ? (
                <EmptySites />
              ) : (
                <div>
                  <Row className="g-3">
                    {(paginatedMySites.length > 0
                      ? paginatedMySites
                      : mySites
                    ).map((site) => (
                      <Col key={site.id} sm={12} md={6} lg={6} xl={4}>
                        <SiteCard
                          type="site-card"
                          key={site.id}
                          site={site}
                          onSiteClick={handleSiteClick}
                        />
                      </Col>
                    ))}
                  </Row>
                  <CustomPagination
                    currentPage={registeredCurrentPage}
                    totalPages={Math.ceil(mySites.length / 12)}
                    data={mySites}
                    itemsPerPage={12}
                    onPageChange={handleRegisteredPageChange}
                    onDataChange={setPaginatedMySites}
                  />
                </div>
              )}
            </Tab>
            <Tab
              eventKey={TAB_KEYS.SHARED_SITES}
              title={
                <span>
                  Shared Sites <span className="shared-site-dot" />
                </span>
              }
            >
              {isLoading || !sites ? (
                <CardSkeleton numberOfCards={3} />
              ) : sharedSites.length === 0 ? (
                <EmptyShare emptyText={NO_SHARED_SITES} />
              ) : (
                <div>
                  <CustomPagination
                    currentPage={sharedCurrentPage}
                    totalPages={Math.ceil(sharedSites.length / 12)}
                    data={sharedSites}
                    itemsPerPage={12}
                    onPageChange={handleSharedPageChange}
                    onDataChange={setPaginatedSharedSites}
                  />
                  <Row className="g-3">
                    {(paginatedSharedSites.length > 0
                      ? paginatedSharedSites
                      : sharedSites
                    ).map((site) => (
                      <Col key={site.id} sm={12} md={6} lg={6} xl={4}>
                        <SiteCard
                          type="shared-site-card"
                          key={site.id}
                          site={site}
                          onSiteClick={handleSiteClick}
                        />
                      </Col>
                    ))}
                  </Row>
                </div>
              )}
            </Tab>
          </Tabs>
        </div>
      )}
      {showShareSite && (
        <ShareSites
          type={SHARE_TYPES.MULTIPLE}
          selected={[]}
          show={showShareSite}
          closeShare={() => setShowShareSite(false)}
        />
      )}
      <SiteFilter
        show={showSiteFilter}
        handleClose={() => setShowSiteFilter(false)}
      />
    </div>
  );
};

export default MySites;
